import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Link, Typography } from '@material-ui/core';
import { TableCellProps } from 'react-virtualized';
import moment from 'moment';

import { getFullName } from 'helpers';
import { Numeric, UserAvatar } from 'components';
import { generateLink } from 'Links';

interface IOptions {
  className: string;
  checkedIds: string[];
  isAllChecked: boolean;
  onAllChecked: (event: ChangeEvent<HTMLInputElement>) => void;
  onChecked: (event: ChangeEvent<HTMLInputElement>) => void;
}

const Columns = (options: IOptions) => {
  const { className, checkedIds, isAllChecked, onAllChecked, onChecked } = options;
  const { t } = useTranslation();

  return [
    {
      dataKey: 'id',
      width: 60,
      className,
      muiHeaderCellProps: { padding: 'checkbox' },
      muiCellProps: { padding: 'checkbox' },
      headerContentRenderer: () => <Checkbox checked={isAllChecked} onChange={onAllChecked} />,
      cellContentRenderer: ({ cellData }: TableCellProps) => (
        <Checkbox name={cellData} checked={checkedIds.includes(cellData)} onChange={onChecked} />
      ),
    },
    {
      dataKey: 'avatar',
      width: 50,
      className,
      label: t('common:name'),
      muiHeaderCellProps: { padding: 'none' },
      muiCellProps: { padding: 'none' },
      cellContentRenderer: ({ rowData }: TableCellProps) => <UserAvatar link user={rowData} />,
    },
    {
      dataKey: 'full_name',
      width: 200,
      className,
      muiHeaderCellProps: { padding: 'none' },
      muiCellProps: { padding: 'none' },
      cellContentRenderer: ({ rowData }: TableCellProps) => (
        <Link
          title={getFullName(rowData)}
          component={generateLink(`/profile/${rowData.id}`)}
          variant="subtitle2"
          noWrap>
          {getFullName(rowData)}
        </Link>
      ),
    },
    {
      dataKey: 'job_title',
      width: 100,
      flexGrow: 1,
      className,
      label: t('common:title'),
      muiHeaderCellProps: { padding: 'none' },
      muiCellProps: { padding: 'none' },
      cellContentRenderer: ({ cellData }: TableCellProps) => (
        <Typography noWrap title={cellData}>
          {cellData}
        </Typography>
      ),
    },
    {
      dataKey: 'department_title',
      width: 100,
      flexGrow: 1,
      className,
      label: t('common:department'),
      muiHeaderCellProps: { padding: 'none' },
      muiCellProps: { padding: 'none' },
      cellContentRenderer: ({ cellData }: TableCellProps) => (
        <Typography noWrap title={cellData}>
          {cellData}
        </Typography>
      ),
    },
    {
      dataKey: 'user_type',
      width: 100,
      flexGrow: 1,
      className,
      label: t('common:role'),
      muiHeaderCellProps: { padding: 'none' },
      muiCellProps: { padding: 'none' },
      cellContentRenderer: ({ cellData }: TableCellProps) => (
        <Typography style={{ textTransform: 'capitalize' }} noWrap title={cellData}>
          {cellData}
        </Typography>
      ),
    },
    {
      dataKey: 'follower_count',
      width: 100,
      flexGrow: 1,
      className,
      label: t('common:reach'),
      muiHeaderCellProps: { padding: 'none' },
      muiCellProps: { padding: 'none' },
      cellContentRenderer: ({ rowData }: TableCellProps) => (
        <Typography noWrap>
          <Numeric value={rowData.follower_count + rowData.reach} />
        </Typography>
      ),
    },
    {
      dataKey: 'last_accessed_at',
      width: 100,
      flexGrow: 1,
      className,
      label: t('admin:lastActive'),
      muiHeaderCellProps: { padding: 'none' },
      muiCellProps: { padding: 'none' },
      cellContentRenderer: ({ cellData }: TableCellProps) => (
        <Typography noWrap>{cellData ? moment.unix(cellData).format('l') : '--'}</Typography>
      ),
    },
  ];
};

export default Columns;
